import React, { useMemo, useState } from 'react';
import { InternalFormSubmission } from '@dill/dill-shared';
import { useAppSelector } from '../../../../redux/store';
import CreditReport from '../CreditApplicationComponents/CreditReport';
import CreditReportModal from '../CreditApplicationComponents/CreditReportModal';
import CreditReportCompanyListModal from '../CreditApplicationComponents/CreditReportCompanyListModal';
import CreditSafeReportCompanyModal from '../CreditApplicationComponents/CreditSafeReportCompanyModal';
import ExperianReportCompanyListModal from '../CreditApplicationComponents/ExperianReportCompanyListModal';
import NACMCreditReportModal from '../CreditApplicationComponents/NACMCreditReportModal';
import CreditSafeReportModal from '../CreditApplicationComponents/CreditSafeReportModal';
import ExperianCreditReportModal from '../CreditApplicationComponents/ExperianCreditReportModal';
import { getBureauScores } from '../CreditApplicationComponents/creditApplicationSummaryUtils';

type InternalFormSubmissionView = Omit<InternalFormSubmission, 'accessToken'>;

// Every pulled report, tagged with its provider — same list CreditApplicationDetails builds.
export const getPulledCreditReports = (submission: InternalFormSubmissionView | null) => [
  ...(submission?.nacmReports ?? []).map((report) => ({ ...report, source: 'NACM' })),
  ...(submission?.creditSafeReports ?? []).map((report) => ({ ...report, source: 'CreditSafe' })),
  ...(submission?.manualCreditReports ?? []).map((report) => ({ ...report, source: 'Manual' })),
  ...(submission?.experianReports ?? []).map((report) => ({ ...report, source: 'Experian' })),
];

// The Summary section's CREDIT SCORE row, same as the credit application's Summary step.
export const InternalFormCreditScoreSummary = ({
  pulledCreditReports,
  onViewFullReport,
}: {
  pulledCreditReports: any[];
  onViewFullReport: () => void;
}) => {
  const bureauScores = getBureauScores(pulledCreditReports);
  const hasAnyScore = !!(bureauScores.creditSafe || bureauScores.experian || bureauScores.nacm);
  const scoreBox = (label: string, score: React.ReactNode, limit?: string) => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-1.5">
      <span className="text-[10px] font-semibold GREY_500-CLR uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
        {score}
        {limit && (
          <span className="text-[10px] font-normal GREY_500-CLR ml-1.5">Limit: {limit}</span>
        )}
      </span>
    </div>
  );
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          Credit Score
        </span>
        <button
          type="button"
          onClick={onViewFullReport}
          className="text-[11px] font-semibold PRIMARY_500-CLR hover:underline">
          {hasAnyScore ? 'View full reports →' : 'Pull a credit report →'}
        </button>
      </div>
      {hasAnyScore ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          {bureauScores.creditSafe &&
            scoreBox(
              'CreditSafe',
              bureauScores.creditSafe.score,
              bureauScores.creditSafe.creditLimit
            )}
          {bureauScores.experian &&
            scoreBox(
              'Experian',
              bureauScores.experian.intelliscore ?? bureauScores.experian.financialScoreRisk,
              bureauScores.experian.creditLimit
            )}
          {bureauScores.nacm && scoreBox('NACM', bureauScores.nacm.score)}
        </div>
      ) : (
        <p className="text-xs GREY_500-CLR">No credit reports pulled yet.</p>
      )}
    </div>
  );
};

// The "Credit Score & Reports" section and every modal behind it, wired to the internal form
// submission instead of a credit application (see internalFormCreditReportService.ts on the
// backend). `onReportsChanged` reloads the submission after a report is pulled or removed.
const InternalFormCreditReports = ({
  submission,
  pulledCreditReports,
  onReportsChanged,
  isProviderPickerOpen,
  setIsProviderPickerOpen,
}: {
  submission: InternalFormSubmissionView;
  pulledCreditReports: any[];
  onReportsChanged: () => void;
  isProviderPickerOpen: boolean;
  setIsProviderPickerOpen: (open: boolean) => void;
}) => {
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [companyList, setCompanyList] = useState<any[]>([]);
  const [companyListError, setCompanyListError] = useState('');
  const [selectedTab, setSelectedTab] = useState<'personal' | 'business' | null>(null);
  const [creditSafePage, setCreditSafePage] = useState(1);
  const [openList, setOpenList] = useState<'nacm' | 'creditsafe' | 'experian' | null>(null);
  const [nacmReport, setNacmReport] = useState<{ id: string; html: string } | null>(null);
  const [creditSafeReport, setCreditSafeReport] = useState<any>(null);
  const [experianReport, setExperianReport] = useState<{
    bin: string;
    data: any;
    bufferData: { data: number[] } | null;
  } | null>(null);

  // Company details for the provider searches, from the form's own answers (nested ones included).
  const companyDetails = useMemo(() => {
    const answers = new Map<string, string>();
    submission.fieldValues.forEach((field) => {
      if (typeof field.value === 'string') answers.set(field.id, field.value.trim());
      (field.fieldValue ?? [])
        .filter((item: any) => !item.hidden && typeof item.value === 'string')
        .forEach((item) => {
          if (!answers.has(item.id)) answers.set(item.id, `${item.value}`.trim());
        });
    });
    const first = (...ids: string[]) => ids.map((id) => answers.get(id)).find(Boolean);
    return {
      companyName: first('customer-or-company-name', 'new-customer-name') ?? '',
      address: first('billing-address', 'physical-address', 'company-address'),
      city: first('billing-city', 'physical-city', 'company-city'),
      state: first('billing-state', 'physical-state', 'company-state'),
      zipCode: first('billing-zip', 'physical-zip', 'company-zip'),
      telephone: first('billing-phone', 'company-phone'),
    };
  }, [submission.fieldValues]);

  if (!currentSupplier) return null;

  return (
    <>
      <CreditReport
        pulledCreditReports={pulledCreditReports}
        onSeeCreditReport={() => setIsProviderPickerOpen(true)}
        onPreviewReport={(data) => {
          if (data.source === 'NACM') {
            setNacmReport({ id: data.id, html: data.html });
          } else if (data.source === 'Experian') {
            setExperianReport({
              bin: data.id,
              data: data.html,
              bufferData: data.bufferData ?? null,
            });
          } else {
            setCreditSafeReport(data.html);
          }
        }}
        creditApplicationId=""
        internalFormSubmissionId={submission.id}
        onReportsChanged={onReportsChanged}
        fieldValues={submission.fieldValues}
        supplier={currentSupplier}
        creditApplication={null}
      />
      <CreditReportModal
        open={isProviderPickerOpen}
        handleClose={() => setIsProviderPickerOpen(false)}
        companyDetails={companyDetails}
        onLearnMore={(data, error, type, tab, page) => {
          setCompanyList(data);
          setCompanyListError(error);
          setSelectedTab(tab);
          if (page) setCreditSafePage(page);
          if (type === 'nacm' || type === 'creditsafe' || type === 'experian') setOpenList(type);
        }}
        creditApplicationId=""
        internalFormSubmissionId={submission.id}
        guarantors={[]}
        overlayZIndex={50}
      />
      <CreditReportCompanyListModal
        open={openList === 'nacm'}
        handleClose={() => setOpenList(null)}
        data={companyList}
        onLearnMore={(data, id) => setNacmReport({ id, html: data })}
        error={companyListError}
        creditApplicationId=""
        internalFormSubmissionId={submission.id}
        onReportsChanged={onReportsChanged}
        companyDetails={companyDetails}
      />
      <CreditSafeReportCompanyModal
        open={openList === 'creditsafe'}
        handleClose={() => setOpenList(null)}
        data={companyList}
        onLearnMore={(data) => setCreditSafeReport(data)}
        error={companyListError}
        creditApplicationId=""
        internalFormSubmissionId={submission.id}
        onReportsChanged={onReportsChanged}
        companyDetails={companyDetails}
        defaultTotal={creditSafePage}
        selectedTab={selectedTab}
        overlayZIndex={51}
      />
      <ExperianReportCompanyListModal
        open={openList === 'experian'}
        handleClose={() => setOpenList(null)}
        data={companyList as any}
        onLearnMore={(data, bin) => setExperianReport({ bin, data, bufferData: null })}
        error={companyListError}
        creditApplicationId=""
        internalFormSubmissionId={submission.id}
        onReportsChanged={onReportsChanged}
        companyDetails={companyDetails}
      />
      <NACMCreditReportModal
        open={!!nacmReport}
        handleClose={() => setNacmReport(null)}
        details={nacmReport?.html ?? ''}
        nacmId={nacmReport?.id ?? ''}
        creditApplicationId=""
      />
      <CreditSafeReportModal
        open={!!creditSafeReport}
        handleClose={() => setCreditSafeReport(null)}
        details={creditSafeReport}
        overlayZIndex={52}
      />
      <ExperianCreditReportModal
        open={!!experianReport}
        handleClose={() => setExperianReport(null)}
        details={experianReport?.data}
        bufferData={experianReport?.bufferData ?? null}
        experianBin={experianReport?.bin ?? ''}
        creditApplicationId=""
      />
    </>
  );
};

export default InternalFormCreditReports;
